import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaUniversity, FaMapMarkerAlt, FaPhone, FaCity, FaUser, FaEdit, FaSearch, FaTh, FaBars, FaTimes, FaMoon, FaSun,FaPlus } from 'react-icons/fa';
import SummaryApi from '../common/index';

const LoaderSVG = () => (
  <svg
    className="animate-spin h-10 w-10 text-blue-600 dark:text-blue-400 mx-auto"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

const CollegesList = () => {
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    code: '',
    city: '',
    contact_person: '',
    contact_number: '',
    status: '',
  });
  const [showFilters, setShowFilters] = useState({
    name: false,
    code: false,
    city: false,
    contact_person: false,
    contact_number: false,
    status: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    sessionStorage.setItem('admin-theme', newDarkMode ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', newDarkMode);
    
    // Dispatch event for other components
    window.dispatchEvent(new Event('themeChanged'));
    window.dispatchEvent(new Event('adminThemeToggled'));
  };

  useEffect(() => {
    fetchColleges();
    window.addEventListener('focus', fetchColleges);
    return () => {
      window.removeEventListener('focus', fetchColleges);
    };
  }, []);

  useEffect(() => {
    if (colleges.length > 0) {
      let filtered = colleges;
      if (searchTerm) {
        filtered = filtered.filter((college) =>
          college.college_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          college.college_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          college.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          college.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          college.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          college.contact_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (college.is_active === 1 ? 'active' : 'inactive').includes(searchTerm.toLowerCase())
        );
      }
      filtered = filtered.filter((college) =>
        (college.college_name?.toLowerCase().includes(columnFilters.name.toLowerCase()) || !columnFilters.name) &&
        (college.college_code?.toLowerCase().includes(columnFilters.code.toLowerCase()) || !columnFilters.code) &&
        (college.city?.toLowerCase().includes(columnFilters.city.toLowerCase()) || !columnFilters.city) &&
        (college.contact_person?.toLowerCase().includes(columnFilters.contact_person.toLowerCase()) || !columnFilters.contact_person) &&
        (college.contact_number?.toLowerCase().includes(columnFilters.contact_number.toLowerCase()) || !columnFilters.contact_number) &&
        (columnFilters.status ? (college.is_active === 1 ? 'active' : 'inactive').includes(columnFilters.status.toLowerCase()) : true)
      );
      setFilteredColleges(filtered);
      setCurrentPage(1);
    }
  }, [colleges, searchTerm, columnFilters]);

  const fetchColleges = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(SummaryApi.CollegeList.url, {
        method: SummaryApi.CollegeList.method,
      });
      const data = await response.json();
      if (data.status) {
        setColleges(data.data || []);
      } else {
        toast.error(data.message || 'Failed to fetch colleges');
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
      toast.error('Error fetching colleges');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatus = (isActive) => (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive === 1 
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      }`}
    >
      {isActive === 1 ? 'Active' : 'Inactive'}
    </span>
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentColleges = filteredColleges.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredColleges.length / itemsPerPage);

  const downloadCSV = () => {
    const headers = ['Name', 'Code', 'Address', 'City', 'Contact Person', 'Contact Number', 'Status'];
    const rows = filteredColleges.map((college) => [
      college.college_name || 'N/A',
      college.college_code || 'N/A',
      college.address || 'N/A',
      college.city || 'N/A',
      college.contact_person || 'N/A',
      college.contact_number || 'N/A',
      college.is_active === 1 ? 'Active' : 'Inactive',
    ]);
    let csvContent =
      'data:text/csv;charset=utf-8,' +
      headers.join(',') +
      '\n' +
      rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'colleges.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFilter = (key) => {
    setShowFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReset = () => {
    setSearchTerm('');
    setColumnFilters({
      name: '',
      code: '',
      city: '',
      contact_person: '',
      contact_number: '',
      status: '',
    });
    setShowFilters({
      name: false,
      code: false,
      city: false,
      contact_person: false,
      contact_number: false,
      status: false,
    });
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className={`min-h-screen p-4 sm:p-6 flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto w-full flex flex-col">
        <div className="mb-6 sm:mb-8 flex justify-between items-center flex-shrink-0">
          <h1 className={`text-2xl sm:text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Colleges List
          </h1>

          <div className="flex items-center space-x-3">
            {/* Dark Mode Toggle */}

            <button
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
              className={`p-2 rounded-md font-medium border transition-colors duration-200 ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : isDarkMode
                    ? 'text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white'
                    : 'text-blue-900 border-blue-900 hover:bg-blue-600 hover:text-white'
              }`}
              title="Grid View"
              type="button"
            >
              <FaTh />
            </button>
            <button
              onClick={() => setViewMode('list')}
              aria-label="List view"
              className={`p-2 rounded-md font-medium border transition-colors duration-200 ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : isDarkMode
                    ? 'text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white'
                    : 'text-blue-900 border-blue-900 hover:bg-blue-600 hover:text-white'
              }`}
              title="List View"
              type="button"
            >
              <FaBars />
            </button>
            <button
              onClick={downloadCSV}
              disabled={filteredColleges.length === 0}
              className={`px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 ${
                filteredColleges.length === 0
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : isDarkMode
                    ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30 text-white'
                    : 'bg-blue-900 hover:bg-blue-700 shadow-lg shadow-blue-700/30 text-white'
              }`}
            >
              Download Excel
            </button>
          </div>
        </div>

        <div className={`rounded-xl shadow-lg overflow-hidden border flex flex-col flex-grow transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-700 shadow-gray-900/50' : 'bg-white border-gray-200'
        }`}>
          <div className={`px-4 py-5 sm:px-6 border-b flex justify-between items-center flex-shrink-0 transition-colors duration-300 ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}>
            <h2 className={`text-lg sm:text-xl font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}></h2>
            <div className="relative w-full sm:w-64 flex items-center space-x-2">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Global Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-3 py-1 border rounded-md focus:outline-none focus:ring-2 transition-all duration-200 pr-9 text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                      : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                />
                <FaSearch
                  className={`absolute right-2 top-2/4 transform -translate-y-1/2 pointer-events-none transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-500'
                  }`}
                  size={16}
                />
              </div>

              <button
                onClick={handleReset}
                disabled={!searchTerm && !Object.values(columnFilters).some((filter) => filter !== '')}
                className={`p-2 rounded-md font-medium border transition-colors duration-200 ${
                  !searchTerm && !Object.values(columnFilters).some((filter) => filter !== '')
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-400 dark:border-gray-600 cursor-not-allowed'
                    : isDarkMode
                      ? 'bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700 shadow-lg shadow-red-600/30'
                      : 'bg-red-500 text-white border-red-500 hover:bg-red-600 hover:border-red-600 shadow-lg shadow-red-500/30'
                }`}
                aria-label="Reset filters"
                title="Reset Filters"
                type="button"
              >
                <FaTimes size={16} />
              </button>
            </div>
          </div>

          <div className="p-3 flex-grow">
            {isLoading ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className={`border rounded-md shadow-sm p-3 flex justify-center items-center transition-colors duration-300 ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                      }`}
                      style={{ height: '250px' }}
                      aria-label="Loading college"
                    >
                      <LoaderSVG />
                    </div>
                  ))}
                </div>
              ) : (
                <table className={`min-w-full border-collapse border text-sm transition-colors duration-300 ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-300'
                }`}>
                  <thead className={`sticky top-0 z-10 transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <tr>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Name</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Code</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Address</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>City</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Contact Person</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Contact Number</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Status</th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr aria-label="Loading colleges">
                      <td colSpan="8" className={`border px-3 py-16 text-center transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600' : 'border-gray-300'
                      }`}>
                        <LoaderSVG />
                      </td>
                    </tr>
                  </tbody>
                </table>
              )
            ) : filteredColleges.length === 0 ? (
         <div className="flex flex-col items-center justify-center py-12">
    <FaUniversity className={`text-5xl mb-4 transition-colors duration-300 ${
      isDarkMode ? 'text-gray-500' : 'text-gray-400'
    }`} />
    <p className={`text-lg mb-6 transition-colors duration-300 ${
      isDarkMode ? 'text-gray-400' : 'text-gray-600'
    }`}>No colleges found for selected criteria.</p>
    <button
      onClick={() => window.location.href = `${window.location.origin}/admin-panel/college-management`}
      className={`px-6 py-3 rounded-md font-medium transition-all shadow-lg flex items-center gap-2 ${
        isDarkMode
          ? "bg-blue-600 hover:bg-blue-700 text-white"
          : "bg-blue-900 hover:bg-blue-800 text-white"
      }`}
    >
      <FaPlus />
      Add College
    </button>
  </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {currentColleges.map((college, index) => (
                  <div
                    key={college.college_id || index}
                    className={`border rounded-lg shadow-sm p-4 hover:shadow-md transition-all duration-200 cursor-default ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h3
                        className={`text-sm font-semibold line-clamp-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-800'
                        }`}
                        title={college.college_name || 'Untitled College'}
                      >
                        {college.college_name || 'Untitled College'}
                      </h3>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => window.open(`/admin-panel/college-management?id=${college.college_id}`, '_blank')}
                          className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 ${
                            isDarkMode ? 'text-blue-400 hover:text-white' : 'text-blue-900 hover:text-blue-800'
                          }`}
                          aria-label={`Edit ${college.college_name || 'college'}`}
                          title={`Edit ${college.college_name || 'college'}`}
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mb-3 flex justify-center">
                      <div className={`h-16 w-16 rounded-full flex items-center justify-center transition-colors duration-300 ${
                        isDarkMode ? 'bg-blue-500' : 'bg-blue-600'
                      }`}>
                        <FaUniversity className="text-white text-2xl" />
                      </div>
                    </div>
                    <div className={`text-[12px] space-y-2 select-none transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      <div className="flex items-center">
                        <FaUniversity className={`mr-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <span className="font-medium">Code:</span>
                        <span className="ml-1">{college.college_code || 'N/A'}</span>
                      </div>
                      <div className="flex items-start">
                        <FaMapMarkerAlt className={`mr-2 mt-1 flex-shrink-0 transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <div>
                          <span className="font-medium">Address:</span>
                          <div className="ml-1 line-clamp-2 break-words">{college.address || 'N/A'}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaCity className={`mr-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <span className="font-medium">City:</span>
                        <span className="ml-1">{college.city || 'N/A'}</span>
                      </div>
                      <div className="flex items-center">
                        <FaUser className={`mr-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <span className="font-medium">Contact:</span>
                        <span className="ml-1 line-clamp-1">{college.contact_person || 'N/A'}</span>
                      </div>
                      <div className="flex items-center">
                        <FaPhone className={`mr-2 transition-colors duration-300 ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <span className="font-medium">Phone:</span>
                        <span className="ml-1">{college.contact_number || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="pt-3">
                      {renderStatus(college.is_active)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <table className={`min-w-full border-collapse border text-sm transition-colors duration-300 ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-300'
                }`}>
                  <thead className={`sticky top-0 z-10 transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <tr>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        <div className="flex flex-col items-center gap-1">
                          Name
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleFilter('name')}
                              className={`transition-colors duration-200 ${
                                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaSearch size={12} />
                            </button>
                            {showFilters.name && (
                              <input
                                type="text"
                                placeholder="Filter Name"
                                value={columnFilters.name}
                                onChange={(e) => setColumnFilters({ ...columnFilters, name: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        <div className="flex flex-col items-center gap-1">
                          Code
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleFilter('code')}
                              className={`transition-colors duration-200 ${
                                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaSearch size={12} />
                            </button>
                            {showFilters.code && (
                              <input
                                type="text"
                                placeholder="Filter Code"
                                value={columnFilters.code}
                                onChange={(e) => setColumnFilters({ ...columnFilters, code: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        Address
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        <div className="flex flex-col items-center gap-1">
                          City
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleFilter('city')}
                              className={`transition-colors duration-200 ${
                                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaSearch size={12} />
                            </button>
                            {showFilters.city && (
                              <input
                                type="text"
                                placeholder="Filter City"
                                value={columnFilters.city}
                                onChange={(e) => setColumnFilters({ ...columnFilters, city: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        <div className="flex flex-col items-center gap-1">
                          Contact Person
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleFilter('contact_person')}
                              className={`transition-colors duration-200 ${
                                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaSearch size={12} />
                            </button>
                            {showFilters.contact_person && (
                              <input
                                type="text"
                                placeholder="Filter Contact"
                                value={columnFilters.contact_person}
                                onChange={(e) => setColumnFilters({ ...columnFilters, contact_person: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        <div className="flex flex-col items-center gap-1">
                          Contact Number
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleFilter('contact_number')}
                              className={`transition-colors duration-200 ${
                                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaSearch size={12} />
                            </button>
                            {showFilters.contact_number && (
                              <input
                                type="text"
                                placeholder="Filter Phone"
                                value={columnFilters.contact_number}
                                onChange={(e) => setColumnFilters({ ...columnFilters, contact_number: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        <div className="flex flex-col items-center gap-1">
                          Status
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleFilter('status')}
                              className={`transition-colors duration-200 ${
                                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaSearch size={12} />
                            </button>
                            {showFilters.status && (
                              <select
                                value={columnFilters.status}
                                onChange={(e) => setColumnFilters({ ...columnFilters, status: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                              >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                      }`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentColleges.map((college, index) => (
                      <tr
                        key={college.college_id || index}
                        className={`transition-colors duration-150 ${
                          isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            <span title={college.college_name || 'Untitled College'} className="transition-colors duration-300">
                              {college.college_name || 'Untitled College'}
                            </span>
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            {college.college_code || 'N/A'}
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                        }`}>
                          <div className="max-h-24 overflow-y-auto break-words">
                            {college.address || 'N/A'}
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            {college.city || 'N/A'}
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            {college.contact_person || 'N/A'}
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            {college.contact_number || 'N/A'}
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            {renderStatus(college.is_active)}
                          </div>
                        </td>
                        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
                          isDarkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}>
                          <div className="max-h-24 overflow-y-auto">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => window.open(`/admin-panel/college-management?id=${college.college_id}`, '_blank')}
                                className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors duration-200 ${
                                  isDarkMode ? 'text-blue-400 hover:text-white' : 'text-blue-600 hover:text-blue-800'
                                }`}
                                aria-label={`Edit ${college.college_name || 'college'}`}
                                title={`Edit ${college.college_name || 'college'}`}
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6 pb-4">
              <button
                onClick={handleFirstPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md border text-sm font-medium transition-all duration-200 ${
                  currentPage === 1 
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                    : isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                      : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                }`}
                aria-label="First page"
              >
                First
              </button>
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md border text-sm font-medium transition-all duration-200 ${
                  currentPage === 1 
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                    : isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                      : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                }`}
                aria-label="Previous page"
              >
                Prev
              </button>
              <span className={`px-3 py-1 font-medium text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md border text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages 
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                    : isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                      : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                }`}
                aria-label="Next page"
              >
                Next
              </button>
              <button
                onClick={handleLastPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md border text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages 
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                    : isDarkMode
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                      : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/30"
                }`}
                aria-label="Last page"
              >
                Last
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      `}</style>
    </div>
  );
};

export default CollegesList;